import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import {
  deriveSuggestedResultLabel,
  readExplicitResultStatus,
  resolveResultLabelDisplay,
  RESULT_STATUS_OPTIONS,
  type SafetyCaseResultLabel,
} from '../../domain/safetyCaseClosure/safetyCaseClosure';
import { reportsApi } from '../../services/reportsApi';
import type { SafetyCaseRecord } from '../../services/reportsApi';
import {
  type SafetyCaseAttestationSaveHandler,
  useRegisterSafetyCaseSaveHandler,
} from '../../domain/safetyCases/safetyCaseAttestationSave';

type FollowUpResultSectionProps = {
  safetyCaseId: string;
  item: SafetyCaseRecord;
  readOnly?: boolean;
  workflowImpactSummary?: Record<string, unknown> | null;
  followupMeasurement?: Record<string, unknown> | null;
  onSaved?: (resultStatus: SafetyCaseResultLabel | null) => void;
  hideSaveButton?: boolean;
  saveHandlerRef?: React.MutableRefObject<SafetyCaseAttestationSaveHandler | null>;
};

export const FollowUpResultSection: React.FC<FollowUpResultSectionProps> = ({
  safetyCaseId,
  item,
  readOnly = false,
  workflowImpactSummary,
  followupMeasurement,
  onSaved,
  hideSaveButton = false,
  saveHandlerRef,
}) => {
  const explicitStatus = useMemo(
    () => readExplicitResultStatus(item.scope),
    [item.scope],
  );
  const suggestedStatus = useMemo(
    () => deriveSuggestedResultLabel({
      safetyCaseStatus: item.safety_case_status,
      impactSummary: workflowImpactSummary as any,
      followupMeasurement,
      scope: item.scope as Record<string, unknown> | undefined,
    }),
    [followupMeasurement, item.safety_case_status, item.scope, workflowImpactSummary],
  );
  const [selectedStatus, setSelectedStatus] = useState<SafetyCaseResultLabel | ''>('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveNotice, setSaveNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSelectedStatus(explicitStatus || '');
    setSaveError(null);
    setSaveNotice(null);
  }, [explicitStatus, item.safety_case_id]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setSaveError(null);
    if (!hideSaveButton) setSaveNotice(null);
    try {
      const response = await reportsApi.patchResultStatus(safetyCaseId, {
        result_status: selectedStatus || null,
      });
      const saved = readExplicitResultStatus(response.safety_case?.scope || response.finding?.scope);
      onSaved?.(saved);
      if (!hideSaveButton) {
        setSaveNotice(saved ? 'Follow-up result saved.' : 'Follow-up result cleared.');
      }
    } catch (error: any) {
      setSaveError(error?.message || 'Unable to save follow-up result right now.');
      throw error;
    } finally {
      setSaving(false);
    }
  }, [hideSaveButton, onSaved, safetyCaseId, selectedStatus]);

  useRegisterSafetyCaseSaveHandler(readOnly ? undefined : saveHandlerRef, handleSave);

  const suggestedLabel = resolveResultLabelDisplay(suggestedStatus);
  const selectedLabel = resolveResultLabelDisplay(selectedStatus || null);

  return (
    <Card variant="outlined" sx={{ borderRadius: 3, mb: 3 }} data-testid="follow-up-result-section">
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.75 }}>
          Follow-up result
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Record the reviewer-attested follow-up outcome. This is separate from measurement math and does not claim individual causality.
        </Typography>
        <Stack spacing={2}>
          {suggestedLabel ? (
            <Alert severity="info" data-testid="follow-up-result-suggested">
              Suggested from measurement: {suggestedLabel}
            </Alert>
          ) : null}
          <FormControl fullWidth size="small">
            <InputLabel id="follow-up-result-status-label">Reviewer-selected result</InputLabel>
            <Select
              labelId="follow-up-result-status-label"
              label="Reviewer-selected result"
              value={selectedStatus}
              onChange={(event) => setSelectedStatus(event.target.value as SafetyCaseResultLabel | '')}
              data-testid="follow-up-result-select"
            >
              <MenuItem value="">
                <em>Not selected</em>
              </MenuItem>
              {RESULT_STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedLabel ? (
            <Typography variant="body2" color="text.secondary" data-testid="follow-up-result-selected-label">
              Reviewer-selected result: {selectedLabel}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No reviewer-selected result is recorded yet.
            </Typography>
          )}
          {saveError ? <Alert severity="error">{saveError}</Alert> : null}
          {saveNotice ? <Alert severity="success">{saveNotice}</Alert> : null}
          <Box>
          {!readOnly && !hideSaveButton ? (
            <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
              {saving ? 'Saving…' : 'Save follow-up result'}
            </Button>
            ) : null}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default FollowUpResultSection;
